import { useQueryClient, type QueryOptions } from '@tanstack/react-query';
import { ms } from 'ms';

import { type UUID } from '~/types/common';

import {
  fetchInvoiceV2PartyCompanies,
  fetchInvoiceV2PartySuggestions,
  type InvoiceV2PartyRole,
  type InvoiceV2PartySuggestion,
  queryKeysInvoice,
} from '~/data/invoice';

import { SelectServerDriven } from './SelectServerDriven';
import { createGetItemData, createLoadOptions } from './utils';

const SUGGESTION_LIMIT = 50;

const getItemId = ({ companyId }: InvoiceV2PartySuggestion) => companyId;
const getOptionLabel = ({ companyId, name }: InvoiceV2PartySuggestion) =>
  name ?? companyId;
const isItemSelectionAllowed = () => true;

type P = {
  readonly isClearable?: boolean;
  readonly isDisabled?: boolean;
  readonly onChange: (companyId: UUID) => void;
  readonly placeholder?: string;
  readonly role: InvoiceV2PartyRole;
  readonly value: UUID;
} & ComponentStyling;

/**
 * Single select for the companies that appear as issuer or buyer on the
 * invoices the user can read, backed by the invoice party suggestion
 * endpoints. Emits the selected company id, or an empty string when the
 * selection is removed.
 */
export const SelectInvoiceV2Party = ({
  className,
  isClearable = true,
  isDisabled = false,
  onChange,
  placeholder,
  role,
  style,
  value,
}: P) => {
  const queryClient = useQueryClient();

  const loadOptions = createLoadOptions<InvoiceV2PartySuggestion>({
    createQueryKey: ({ searchString }) =>
      queryKeysInvoice.getPartySuggestions({
        limit: SUGGESTION_LIMIT,
        query: searchString || undefined,
        role,
      }),
    async fetchItems({ searchString }) {
      const { data } = await fetchInvoiceV2PartySuggestions({
        queryParams: {
          limit: SUGGESTION_LIMIT,
          query: searchString || undefined,
        },
        role,
      });

      // Validation failures can return data with an unknown runtime shape.
      const suggestions = Array.isArray(data) ? data : [];

      return {
        count: suggestions.length,
        data: suggestions,
        hasNextPage: false,
      };
    },
    getOptionLabel,
    getOptionValue: getItemId,
    queryClient,
    queryOptions: {
      staleTime: ms('60s'),
    } as Partial<QueryOptions>,
  });

  /**
   * Resolves a selected company id to its name, e.g. when restoring a saved
   * filter. An id that comes back unresolved throws, which leaves the select
   * showing no selection rather than a bare id.
   */
  const getItemData = createGetItemData<InvoiceV2PartySuggestion>({
    createQueryKey: (companyId) => [
      ...queryKeysInvoice.getPartyCompany({ companyId }),
    ],
    async fetchItem(companyId) {
      const { data } = await fetchInvoiceV2PartyCompanies([companyId]);
      const company = Array.isArray(data)
        ? data.find((item) => item.companyId === companyId)
        : undefined;

      if (!company) {
        throw new Error(`Invoice party ${companyId} could not be resolved`);
      }

      return company;
    },
    getItemId,
    queryClient,
    queryKeyBase: [queryKeysInvoice.base(), 'party-suggestions'],
  });

  // The select is single mode, so one string always arrives; the array form
  // is only admitted by the prop type.
  const handleChange = (companyId: string | string[]) => {
    onChange([companyId].flat()[0] ?? '');
  };

  return (
    <SelectServerDriven<InvoiceV2PartySuggestion>
      className={className}
      getItemData={getItemData}
      getOptionLabel={getOptionLabel}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isItemSelectionAllowed={isItemSelectionAllowed}
      isMultiSelect={false}
      loadOptions={loadOptions}
      placeholder={placeholder}
      style={style}
      value={value}
      onChange={handleChange}
    />
  );
};
